import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { useWatch } from "antd/es/form/Form";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import Switch from "antd/es/switch";
import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import { Alert, NumberField, TextField } from "@mapmycustomers/ui";

import CurrencyInput from "@app/component/input/CurrencyInput";
import RadioGroup from "@app/component/input/RadioGroup";
import TargetType from "@app/enum/dashboard/TargetType";
import { isMember } from "@app/store/iam/util";
import { getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { GoalConfiguration } from "@app/types/dashboard/CardConfiguration";

import styles from "./GoalTargetType.module.scss";
import { messages, targetTypeDescription, targetTypeDisplayName } from "./messages";

interface Props {
  currencyType?: boolean;
  goalConfiguration?: GoalConfiguration;
  teamId: Team["id"];
  teams: Team[];
}

const GoalTargetType: React.FC<Props> = ({ currencyType, goalConfiguration, teamId, teams }) => {
  const intl = useIntl();
  const form = useFormInstance();

  const target: number = useWatch(["goal", "target"]) ?? 0;
  const targetType: TargetType = useWatch(["goal", "type"]) ?? TargetType.TEAM;
  const defaultTarget: number = useWatch(["goal", "defaultTarget"]) ?? 0;

  const [enabled, setEnabled] = useState(targetType !== TargetType.TEAM);
  useEffect(() => {
    if (targetType !== TargetType.TEAM) {
      setEnabled(true);
    }
  }, [targetType]);
  const handleChangeEnabled = useCallback(
    (enabled) => {
      if (!enabled) {
        form.setFieldsValue({ goal: { type: TargetType.TEAM } });
      }
      setEnabled(enabled);
    },
    [form]
  );

  const team = useMemo(() => teams.find(({ id }) => id === teamId), [teamId, teams]);
  const teamSize = team?.users.length ?? 0;

  const typeOptions = useMemo(
    () =>
      (Object.values(TargetType) as TargetType[]).map((type) => ({
        label: intl.formatMessage(targetTypeDisplayName[type]),
        value: type,
      })),
    [intl]
  );

  const handleTargetTypeChange = useCallback(
    (type: TargetType) => {
      if (type === TargetType.INDIVIDUAL) {
        form.setFieldsValue({
          goal: { defaultTarget: teamSize ? Math.ceil(target / teamSize) : 0 },
        });
      } else {
        const size =
          type === TargetType.TEAM
            ? teamSize
            : type === TargetType.MEMBERS
            ? team?.users.filter(isMember).length
            : 0;
        const amount = size ? Math.ceil(target / size) : 0;
        form.setFieldValue(["goal", "defaultTarget"], amount);
      }
    },
    [form, teamSize, target, team?.users]
  );

  let showCountBanner = false;
  if (targetType === TargetType.INDIVIDUAL) {
    const sumOfIndividualGoals =
      team?.users.reduce(
        (result, user) =>
          result + (goalConfiguration?.individualUserGoals?.[user.id] ?? defaultTarget ?? 0),
        0
      ) ?? 0;
    showCountBanner = sumOfIndividualGoals < target;
  }

  return (
    <div className={styles.container}>
      <label className={styles.switch}>
        <span>Also set a goal for each team member</span>
        <Switch checked={enabled} onChange={handleChangeEnabled} />
      </label>
      <Form.Item hidden={!enabled} name={["goal", "type"]} noStyle preserve>
        <RadioGroup<TargetType>
          defaultValue={TargetType.TEAM}
          onChange={handleTargetTypeChange}
          options={typeOptions}
          vertical
        />
      </Form.Item>
      {enabled && (
        <>
          <Alert
            message={intl.formatMessage(targetTypeDescription[targetType], {
              b: (text: ReactNode[]) => <b>{text}</b>,
              team: team?.name,
            })}
            showIcon
            type="info"
          />
          {showCountBanner && (
            <Alert
              message={intl.formatMessage({
                id: "dashboard.editModal.configuration.goal.targetType.individual.oversizeAlert",
                defaultMessage:
                  "Please note, the sum of all individual member goals is less than the team goal. Consider setting higher individual goals in order to meet your team goal.",
                description:
                  "A banner to show when sum of individual goals is bigger than total goal, for individual Team goal target in Create/Edit Card modal on the Dashboard page",
              })}
              showIcon
              type="warning"
            />
          )}
          {targetType === TargetType.INDIVIDUAL ? (
            <Form.Item
              label={intl.formatMessage(messages.defaultTargetLabel)}
              name={["goal", "defaultTarget"]}
            >
              {currencyType ? (
                <CurrencyInput min={0} />
              ) : (
                <NumberField fullWidth min={0} step={1} />
              )}
            </Form.Item>
          ) : (
            <Form.Item
              label={intl.formatMessage(messages.individualGoal)}
              name={["goal", "defaultTarget"]}
            >
              {currencyType ? <CurrencyInput disabled /> : <TextField locked />}
            </Form.Item>
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  teams: getTeams(state),
});

export default connect(mapStateToProps)(GoalTargetType);
